import { useRef, useState } from 'react';
import {
  QueryParamConfigMap,
  useQueryParams as useQueryParamsLib,
} from 'use-query-params';

import { useTime } from '@/contexts';

type InputOptions = {
  fromField?: string;
  toField?: string;
  fromAndToOptional?: boolean;
  withTime?: boolean;
};

const getOptions = (inputOptions: InputOptions) => ({
  fromField: inputOptions.fromField ?? 'from',
  toField: inputOptions.toField ?? 'to',
  fromAndToOptional: inputOptions.fromAndToOptional ?? false,
  withTime: inputOptions.withTime ?? false,
});

// Check if any parameter (except pagination params) is changing
const isPaginationParam = (key: string) => key === 'after' || key === 'before';

export const useQueryParams = <T extends QueryParamConfigMap>(
  params: T,
  inputOptions: InputOptions = {},
) => {
  const options = getOptions(inputOptions);
  const [query, setQuery] = useQueryParamsLib<T>(params);

  // Track previous query values to detect any changes
  const previousValuesRef = useRef<Record<string, string>>({});

  // Time-related logic (only used when withTime is true)
  const { time } = useTime();
  const from = (options.fromAndToOptional && time.nullableFrom) || time.from;
  const to = (options.fromAndToOptional && time.nullableTo) || time.to;

  const [useLocalTime, setUseLocalTime] = useState(
    !!(query[options.fromField] || query[options.toField]),
  );

  // Enhanced updater function with pagination reset logic
  const enhancedUpdater: typeof setQuery = (value, updateType) => {
    let resultValue = value;
    if (typeof value === 'function') {
      resultValue = value(query);
    }

    // Handle time fields only if withTime is true
    if (
      options.withTime &&
      (options.fromField in resultValue || options.toField in resultValue)
    ) {
      setUseLocalTime(true);
    }

    const nonPaginationEntries = Object.entries(resultValue).filter(
      ([key]) => !isPaginationParam(key),
    );

    const {
      hasChanges: hasNonPaginationChanges,
      updatedValues: updatedPreviousValues,
    } = nonPaginationEntries.reduce(
      (acc, [key, newValue]) => {
        const previousValue = previousValuesRef.current[key];

        if (newValue !== previousValue) {
          acc.hasChanges = true;
          acc.updatedValues[key] = newValue;
        }

        return acc;
      },
      {
        hasChanges: false,
        updatedValues: { ...previousValuesRef.current },
      },
    );

    // If any non-pagination parameter changed, remove pagination
    if (hasNonPaginationChanges) {
      const updatedResult = { ...resultValue };

      if ('after' in params) {
        (updatedResult as Record<string, unknown>).after = undefined;
      }

      if ('before' in params) {
        (updatedResult as Record<string, unknown>).before = undefined;
      }

      resultValue = updatedResult;

      // Update all previous values
      previousValuesRef.current = updatedPreviousValues;
    }

    setQuery(resultValue, updateType);
  };

  // Return different filter objects based on withTime option
  const filter = options.withTime
    ? {
        ...query,
        ...(useLocalTime
          ? {}
          : { [options.fromField]: from, [options.toField]: to }),
      }
    : query;

  return [filter, enhancedUpdater] as const;
};
